import { useAtom, useAction } from '@reatom/npm-react'
import { capitalize } from 'radash'
import { useMemo } from 'react'
import styled from 'styled-components'

import { AsyncStateN, Array } from '@blade/std-lib'
import { useTranslation } from '@root/shared/localization'
import { Dialog, FadeOverflow, InfoMessage } from '@root/shared/ui-kit'
import { CurrencyCardCompact } from '@root/widgets/fun/currency-card/ui/currency-card-compact'
import { CurrencyCardCompactSkeleton } from '@root/widgets/fun/currency-card/ui/currency-card-compact-skeleton'

import { lastCreatedCoins, profileCoinsModal } from '../../atoms'

import type { DelugeId } from '@root/entities/user-short'
import type { ClassNameProps } from '@root/shared/react'

export const ProfileCoinsModal = () => {
  const { t } = useTranslation()
  const [modal, setModal] = useAtom(profileCoinsModal)

  const title = capitalize(t('common.created_coins'))

  return (
    <Dialog
      describedBy={title}
      open={!!modal}
      onOpenChange={() => setModal(undefined)}
    >
      <Dialog.Header title={title} />

      {modal ? (
        <ProfileCoinsModalContent delugeId={modal.delugeId} />
      ) : undefined}
    </Dialog>
  )
}

type ProfileCoinsProps = {
  delugeId: DelugeId
} & ClassNameProps

export const ProfileCoinsModalContent = ({
  delugeId,
  className,
}: ProfileCoinsProps) => {
  const model = useMemo(() => lastCreatedCoins({ delugeId }), [delugeId])
  const [lastCreatedCoinsState] = useAtom(model.lastCreatedCoinsStateAtom)
  const refreshFilter = useAction(model.lastCreatedCoinsStateAtom.refresh)

  return (
    <Root className={className}>
      <Body variant="vertical" blockEnd={65}>
        {AsyncStateN.matchWaiting(lastCreatedCoinsState, {
          Error: () => (
            <InfoContainer>
              <InfoMessage
                intent={'error'}
                variant="dt"
                onRetry={refreshFilter}
              />
            </InfoContainer>
          ),
          Waiting: () => (
            <ItemsList>
              {Array.range(0, 3).map((x) => (
                <CurrencyCardCompactSkeleton key={x} />
              ))}
            </ItemsList>
          ),
          Success: (pairs) =>
            pairs.length <= 0 ? (
              <InfoContainer>
                <InfoMessage intent={'empty'} variant="dt" />
              </InfoContainer>
            ) : (
              <ItemsList>
                {pairs.map((pair) => (
                  <CurrencyCardCompact pair={pair} key={pair.address} />
                ))}
              </ItemsList>
            ),
        })}
      </Body>
    </Root>
  )
}

const Root = styled.div`
  display: grid;

  overflow: hidden;
  width: 440px;
  height: 460px;
  padding: 12px;
`

const Body = styled(FadeOverflow)`
  overflow: auto;
  display: grid;
`

const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: start;
`

const InfoContainer = styled.div`
  padding: 12px 0;
  display: grid;
`
